import React, { useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { XYZ, Vector as VectorSource } from 'ol/source';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import Overlay from 'ol/Overlay';
import pako from 'pako';
import { Spinner } from 'react-bootstrap';
import { fieldAliases } from '../fieldAliases';
import {
  getCapabilityInterpretation,
  sortCartaSueloEntries,
  parseCapabilityFromCap,
} from '../utils/soilCapabilityInterpretation';

const layerConfigs = [
  {
    name: 'Variables Granulometricas',
    key: 'granulometria',
    url: 'https://aitslugfbrefcpvogjow.supabase.co/storage/v1/object/public/files/geojson/VariablesGranulometricas_1.geojson.gz',
    color: '#ffcc33',
    visible: false,
    compressed: true,
  },
  {
    name: 'Cartas de Suelo',
    key: 'cartas_suelo',
    url: 'https://aitslugfbrefcpvogjow.supabase.co/storage/v1/object/public/files/geojson/CartasdeSuelo_1.geojson.gz',
    color: '#ffcc33',
    visible: false,
    compressed: true,
  },
];

async function fetchGeoJSON(url, compressed) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} - ${res.statusText}`);

  if (compressed) {
    const buf = await res.arrayBuffer();
    const jsonStr = pako.ungzip(buf, { to: 'string' });
    return JSON.parse(jsonStr);
  }

  return await res.json();
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function classifyUSDA(sand, silt, clay) {
  if ([sand, silt, clay].some((x) => x === null || x < 0 || x > 100)) return 'Valores Inválidos';

  const sum = sand + silt + clay;
  if (sum <= 0) return 'Valores Inválidos';

  const S = (sand * 100) / sum;
  const Si = (silt * 100) / sum;
  const C = (clay * 100) / sum;

  if (C >= 40 && S <= 45 && Si <= 40) return 'Arcilloso';
  if (C >= 35 && S > 45) return 'Arcillo Arenoso';
  if (C >= 40 && Si > 40) return 'Arcillo Limoso';

  if (C >= 27 && C < 40 && S > 20 && S <= 45 && Si <= 53) return 'Franco Arcilloso';
  if (C >= 27 && C < 40 && Si > 40 && S <= 20) return 'Franco Arcillo Limoso';
  if (C >= 20 && C < 35 && S > 45 && Si <= 28) return 'Franco Arcillo Arenoso';

  if (C >= 7 && C < 27 && Si >= 28 && Si < 50 && S <= 52) return 'Franco';
  if (C >= 7 && C < 27 && Si >= 50 && Si < 80 && S <= 50) return 'Franco Limoso';
  if (C < 12 && Si >= 80) return 'Limoso';

  if (C < 20 && S >= 52 && Si < 50) return 'Franco Arenoso';
  if (C < 7 && S > 85 && Si < 15) return 'Arenoso';
  if (C < 10 && S >= 70 && S <= 90 && Si <= 30) return 'Arena Franca';

  return 'Clase No Determinada (Revisar Límites)';
}

const TEXTURE_INFO = {
  Arenoso: {
    title: 'Arenoso',
    bullets: [
      'Alta infiltración y drenaje; baja retención de agua.',
      'Baja capacidad de intercambio (CIC) y mayor riesgo de pérdidas por lixiviación.',
      'Se calienta rápido; útil para siembras tempranas, pero exige manejo fino del agua.',
    ],
    management: [
      'Aumentar materia orgánica (coberturas, compost, rotaciones).',
      'Fertilización fraccionada (N especialmente) para reducir pérdidas.',
      'Riego más frecuente y con menor lámina por evento.',
    ],
  },
  'Arena Franca': {
    title: 'Arena Franca',
    bullets: [
      'Drenaje bueno con algo más de retención de agua que un arenoso puro.',
      'Riesgo moderado de lixiviación; estructura generalmente suelta.',
    ],
    management: [
      'Mantener cobertura para mejorar retención y reducir evaporación.',
      'Fraccionar fertilización nitrogenada en ambientes de alta lluvia/riego.',
    ],
  },
  'Franco Arenoso': {
    title: 'Franco Arenoso',
    bullets: [
      'Buen balance entre infiltración y retención; suele responder bien a manejo de MO.',
      'Menor riesgo de encharcamiento; puede secarse relativamente rápido.',
    ],
    management: [
      'Monitorear humedad; evitar estrés hídrico en períodos críticos.',
      'Aporte de MO para mejorar estabilidad de agregados.',
    ],
  },
  Franco: {
    title: 'Franco',
    bullets: [
      'Textura “equilibrada”: buen compromiso entre retención de agua y aireación.',
      'Generalmente favorable para la mayoría de cultivos.',
    ],
    management: [
      'Evitar compactación (no entrar con exceso de humedad).',
      'Mantener materia orgánica y cobertura para conservar estructura.',
    ],
  },
  'Franco Limoso': {
    title: 'Franco Limoso',
    bullets: [
      'Buena retención de agua, pero puede ser susceptible a encostramiento superficial.',
      'Riesgo moderado de compactación y sellado si se trabaja húmedo.',
    ],
    management: [
      'Cobertura permanente (rastrojo/cultivos de servicio) para reducir encostramiento.',
      'Tránsito controlado y evitar labores con alta humedad.',
      'Cuidar estructura con rotaciones y aporte de MO.',
    ],
  },
  Limoso: {
    title: 'Limoso',
    bullets: [
      'Alta susceptibilidad a sellado/encostramiento y erosión hídrica.',
      'Retención moderada/alta, pero con drenaje variable según estructura.',
    ],
    management: [
      'Cobertura y siembra directa para evitar encostramiento.',
      'Manejo de escurrimiento (curvas de nivel, terrazas donde aplique).',
      'Evitar laboreo intensivo; priorizar MO y estabilidad estructural.',
    ],
  },
  'Franco Arcilloso': {
    title: 'Franco Arcilloso',
    bullets: [
      'Alta retención de agua y nutrientes; aireación más limitada que texturas francas.',
      'Riesgo de compactación si hay tránsito con humedad.',
    ],
    management: [
      'Evitar ingreso con suelo húmedo; tráfico controlado.',
      'Coberturas y rotaciones con raíces profundas para mejorar estructura.',
      'Considerar drenaje si hay anegamientos frecuentes.',
    ],
  },
  'Franco Arcillo Arenoso': {
    title: 'Franco Arcillo Arenoso',
    bullets: [
      'Retención buena con infiltración moderada; menos plástico que arcillas pesadas.',
      'Puede compactar si se trabaja húmedo.',
    ],
    management: [
      'Minimizar labores en condiciones húmedas.',
      'Aporte de MO para mejorar porosidad y estabilidad.',
    ],
  },
  'Franco Arcillo Limoso': {
    title: 'Franco Arcillo Limoso',
    bullets: [
      'Muy buena retención; mayor riesgo de sellado/compactación y drenaje lento.',
      'Puede presentar encharcamiento en eventos intensos si la estructura es pobre.',
    ],
    management: [
      'Coberturas + rotaciones para mejorar macroporos.',
      'Tránsito controlado; evitar pisoteo/maquinaria en húmedo.',
      'Evaluar drenaje superficial/subsuperficial si el ambiente lo requiere.',
    ],
  },
  Arcilloso: {
    title: 'Arcilloso',
    bullets: [
      'Muy alta retención de agua y nutrientes, pero drenaje lento y aireación limitada.',
      'Alta plasticidad: se compacta y “se amasa” fácilmente cuando está húmedo.',
    ],
    management: [
      'Evitar labores y tránsito con humedad alta (clave).',
      'Mejorar estructura con MO, raíces (coberturas) y rotaciones.',
      'Monitorear anegamiento; considerar drenaje si aplica.',
    ],
  },
  'Arcillo Arenoso': {
    title: 'Arcillo Arenoso',
    bullets: [
      'Retención alta con algo más de drenaje que arcilla pura.',
      'Riesgo de compactación moderado/alto si se trabaja húmedo.',
    ],
    management: [
      'Aporte de MO y manejo de tránsito para sostener porosidad.',
      'Evitar labores en húmedo.',
    ],
  },
  'Arcillo Limoso': {
    title: 'Arcillo Limoso',
    bullets: [
      'Retención muy alta y drenaje lento; propenso a compactación y sellado.',
      'Puede ser sensible a erosión superficial si queda desnudo.',
    ],
    management: [
      'Cobertura permanente y mínima remoción.',
      'Tránsito controlado; evitar compactación.',
      'Evaluar drenaje en zonas con anegamiento recurrente.',
    ],
  },
};

function getAlias(layerName, field) {
  return fieldAliases?.[layerName]?.[field] ?? field;
}

function getTextureInterpretation(texture) {
  if (!texture) return null;
  const info = TEXTURE_INFO[texture];
  if (!info) {
    return {
      title: texture,
      bullets: ['No hay interpretación configurada para esta clase.'],
      management: [],
    };
  }
  return info;
}

function getVisibleLayersByMode() {
  return {
    'Variables Granulometricas': false,
    'Cartas de Suelo': false,
  };
}

export default function QgisMap({
  mode = 'granulometria',
  granulometria,
  cartaSuelo,
  onSaveGranulometry,
  onSaveCartaSuelo,
}) {
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const highlightSourceRef = useRef(null);

  const [mapObj, setMapObj] = useState(null);
  const [layerStates, setLayerStates] = useState({});
  const [layerLoadingState, setLayerLoadingState] = useState({});
  const [selected, setSelected] = useState(null);
  const [texture, setTexture] = useState(null);
  const [lastClickLonLat, setLastClickLonLat] = useState(null);
  const [textureInfo, setTextureInfo] = useState(null);

  const isGranulometriaMode = mode === 'granulometria';
  const savedData = isGranulometriaMode ? granulometria : cartaSuelo;

  useEffect(() => {
    const visibleLayers = getVisibleLayersByMode();

    const loadLayerData = async (source, cfg, projection) => {
      if (!source || source.get('loadedOnce') || source.get('loading')) return;

      source.set('loading', true, true);
      setLayerLoadingState((prev) => ({ ...prev, [cfg.name]: 'loading' }));

      try {
        const geojson = await fetchGeoJSON(cfg.url, cfg.compressed);

        const features = new GeoJSON().readFeatures(geojson, {
          dataProjection: 'EPSG:4326',
          featureProjection: projection,
        });

        for (const f of features) {
          f.set('_layerName', cfg.name, true);
          f.set('_layerKey', cfg.key, true);
        }

        source.addFeatures(features);
        source.set('loadedOnce', true, true);
        setLayerLoadingState((prev) => ({ ...prev, [cfg.name]: 'loaded' }));
      } catch (err) {
        console.error('Error cargando capa:', cfg.name, err);
        setLayerLoadingState((prev) => ({ ...prev, [cfg.name]: 'error' }));
      } finally {
        source.set('loading', false, true);
      }
    };

    const baseLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        attributions: '© Google Maps',
      }),
    });

    const layersByName = {};

    const vectorLayers = layerConfigs.map((cfg) => {
      const source = new VectorSource();

      source.set('cfg', cfg, true);
      source.set('loadedOnce', false, true);
      source.set('loading', false, true);

      const vector = new VectorLayer({
        source,
        visible: visibleLayers[cfg.name] ?? false,
        style: (feature) => {
          const geometryType = feature.getGeometry().getType();
          return new Style({
            image:
              geometryType === 'Point'
                ? new CircleStyle({
                  radius: 5,
                  fill: new Fill({ color: cfg.color }),
                  stroke: new Stroke({ color: '#000', width: 1 }),
                })
                : undefined,
            fill: new Fill({ color: `${cfg.color}44` }),
            stroke: new Stroke({ color: cfg.color, width: 1 }),
          });
        },
      });

      layersByName[cfg.name] = vector;
      return vector;
    });

    const highlightSource = new VectorSource();
    highlightSourceRef.current = highlightSource;

    const highlightLayer = new VectorLayer({
      source: highlightSource,
      zIndex: 9999,
      style: (feature) => {
        const t = feature.getGeometry().getType();
        return new Style({
          image:
            t === 'Point'
              ? new CircleStyle({
                radius: 9,
                fill: new Fill({ color: 'rgba(63, 127, 67, 0.35)' }),
                stroke: new Stroke({ color: 'rgba(63, 127, 67, 1)', width: 3 }),
              })
              : undefined,
          fill: new Fill({ color: 'rgba(63, 127, 67, 0.15)' }),
          stroke: new Stroke({ color: 'rgba(63, 127, 67, 1)', width: 4 }),
        });
      },
    });

    const map = new Map({
      target: mapRef.current,
      layers: [baseLayer, ...vectorLayers, highlightLayer],
      view: new View({
        center: fromLonLat([-62.123843, -31.424238]),
        zoom: 14,
      }),
    });

    const overlay = new Overlay({
      element: popupRef.current,
      autoPan: true,
      autoPanAnimation: { duration: 250 },
    });

    map.addOverlay(overlay);

    const highlightFeature = (feature) => {
      const hs = highlightSourceRef.current;
      if (!hs) return;
      hs.clear();
      if (feature) hs.addFeature(feature.clone());
    };

    map.on('click', (evt) => {
      const lonLatClicked = toLonLat(evt.coordinate);
      setLastClickLonLat(lonLatClicked);

      const feature = map.forEachFeatureAtPixel(evt.pixel, (feat) => feat, {
        hitTolerance: 4,
      });

      if (!feature) {
        highlightFeature(null);
        if (popupRef.current) popupRef.current.style.display = 'none';
        setSelected(null);
        if (isGranulometriaMode) {
          setTexture(null);
          setTextureInfo(null);
        }
        return;
      }

      const featureLayerKey = feature.get('_layerKey');
      if (isGranulometriaMode && featureLayerKey !== 'granulometria') return;
      if (!isGranulometriaMode && featureLayerKey !== 'cartas_suelo') return;

      highlightFeature(feature);

      const layerName = feature.get('_layerName') || 'Capa';
      const props = feature.getProperties();

      const Arena = toNum(props.Arena ?? props.arena);
      const Limo = toNum(props.Limo ?? props.limo);
      const Arcilla = toNum(props.Arcilla ?? props.arcilla);

      const cleanedProps = Object.entries(props).reduce((acc, [key, value]) => {
        if (
          key !== 'geometry' &&
          key !== '_layerName' &&
          key !== '_layerKey' &&
          value !== null &&
          value !== undefined &&
          value !== ''
        ) {
          acc[key] = value;
        }
        return acc;
      }, {});

      let normalizedProps = cleanedProps;

      if (!isGranulometriaMode) {
        const parsedCap = parseCapabilityFromCap(cleanedProps.cap ?? cleanedProps.cap_uso);

        normalizedProps = {
          ...cleanedProps,
          clase: cleanedProps.clase ?? parsedCap.clase,
          subclase: cleanedProps.subclase ?? parsedCap.subclase,
        };
      }

      const capabilityInterpretation =
        !isGranulometriaMode ? getCapabilityInterpretation(normalizedProps) : null;

      setSelected({
        Arena,
        Limo,
        Arcilla,
        layerName,
        featureId: feature.getId?.() ?? null,
        props: normalizedProps,
        interpretation: capabilityInterpretation,
      });

      if (isGranulometriaMode) {
        setTexture(null);
        setTextureInfo(null);

        popupRef.current.innerHTML = `
          <div class="da-map-popup">
            <div class="da-map-popup__title">${layerName}</div>
            <ul class="da-map-popup__list">
              <li><span>Arcilla (%)</span><b>${Arcilla ?? ''}</b></li>
              <li><span>Arena (%)</span><b>${Arena ?? ''}</b></li>
              <li><span>Limo (%)</span><b>${Limo ?? ''}</b></li>
            </ul>
            <button class="da-map-popup__close" id="popupCloseBtn" type="button">Cerrar</button>
          </div>
        `;
      } else {
        const propsHtml = sortCartaSueloEntries(
          Object.entries(normalizedProps).filter(([k]) => k !== 'cap_uso')
        )
          .slice(0, 8)
          .map(([k, v]) => {
            const label = getAlias(layerName, k);
            return `<li><span>${label}</span><b>${v}</b></li>`;
          })
          .join('');

        popupRef.current.innerHTML = `
          <div class="da-map-popup">
            <div class="da-map-popup__title">${layerName}</div>
            <ul class="da-map-popup__list">
              ${propsHtml || '<li><span>Sin datos</span><b>—</b></li>'}
            </ul>
            <button class="da-map-popup__close" id="popupCloseBtn" type="button">Cerrar</button>
          </div>
        `;
      }

      overlay.setPosition(evt.coordinate);
      popupRef.current.style.display = 'block';

      const btn = popupRef.current.querySelector('#popupCloseBtn');
      if (btn) btn.onclick = () => (popupRef.current.style.display = 'none');
    });

    setMapObj({
      map,
      layers: layersByName,
      loadLayerData,
    });

    setLayerStates(
      layerConfigs.reduce((acc, l) => {
        acc[l.name] = visibleLayers[l.name] ?? false;
        return acc;
      }, {})
    );

    setLayerLoadingState(
      layerConfigs.reduce((acc, l) => {
        acc[l.name] = 'idle';
        return acc;
      }, {})
    );

    return () => {
      map.setTarget(null);
    };
  }, [mode, isGranulometriaMode]);

  useEffect(() => {
    if (!savedData) return;

    if (isGranulometriaMode) {
      setSelected({
        Arena: savedData.arena ?? null,
        Limo: savedData.limo ?? null,
        Arcilla: savedData.arcilla ?? null,
        layerName: savedData.layerName ?? 'Variables Granulometricas',
        featureId: savedData.featureId ?? null,
        props: savedData.props ?? {},
      });

      setTexture(savedData.textura ?? null);
      setTextureInfo(savedData.interpretacion ?? getTextureInterpretation(savedData.textura));
    } else {
      const savedProps = savedData.props ?? {};
      const parsedCap = parseCapabilityFromCap(savedProps.cap ?? savedProps.cap_uso);

      const normalizedSavedProps = {
        ...savedProps,
        clase: savedProps.clase ?? parsedCap.clase,
        subclase: savedProps.subclase ?? parsedCap.subclase,
      };

      setSelected({
        Arena: null,
        Limo: null,
        Arcilla: null,
        layerName: savedData.layerName ?? 'Cartas de Suelo',
        featureId: savedData.featureId ?? null,
        props: normalizedSavedProps,
        interpretation:
          savedData.interpretation ?? getCapabilityInterpretation(normalizedSavedProps),
      });
    }

    setLastClickLonLat(savedData.lonLat ?? null);

    if (mapObj?.map && savedData.lonLat) {
      mapObj.map.getView().animate({
        center: fromLonLat(savedData.lonLat),
        zoom: 15,
        duration: 600,
      });
    }

    const targetLayerName = isGranulometriaMode
      ? 'Variables Granulometricas'
      : 'Cartas de Suelo';

    if (mapObj?.map && savedData.lonLat && layerLoadingState[targetLayerName] === 'loaded') {
      const timeoutId = setTimeout(() => {
        const map = mapObj.map;
        const coord = fromLonLat(savedData.lonLat);
        const pixelCenter = map.getPixelFromCoordinate(coord);
        if (!pixelCenter) return;

        const hs = highlightSourceRef.current;
        if (hs) hs.clear();

        const radiusPx = 14;
        let found = null;

        for (let dx = -radiusPx; dx <= radiusPx && !found; dx += 2) {
          for (let dy = -radiusPx; dy <= radiusPx && !found; dy += 2) {
            const px = [pixelCenter[0] + dx, pixelCenter[1] + dy];

            const f = map.forEachFeatureAtPixel(px, (feat) => feat, {
              hitTolerance: 2,
            });

            if (!f) continue;

            const featureLayerKey = f.get('_layerKey');
            if (isGranulometriaMode && featureLayerKey === 'granulometria') {
              found = f;
            }
            if (!isGranulometriaMode && featureLayerKey === 'cartas_suelo') {
              found = f;
            }
          }
        }

        if (found && hs) hs.addFeature(found.clone());
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [savedData, mapObj, layerLoadingState, isGranulometriaMode]);

  const toggleLayer = async (name) => {
    if (!mapObj) return;

    const layer = mapObj.layers[name];
    if (!layer) return;

    const newVisibility = !layer.getVisible();
    layer.setVisible(newVisibility);

    setLayerStates((prev) => ({ ...prev, [name]: newVisibility }));

    if (newVisibility) {
      const source = layer.getSource();
      const cfg = source.get('cfg');

      if (!source.get('loadedOnce') && !source.get('loading')) {
        const projection = mapObj.map.getView().getProjection();
        await mapObj.loadLayerData(source, cfg, projection);
      }
    }
  };

  const onCalculate = () => {
    if (!selected) {
      setTexture('Primero seleccioná un punto/polígono en el mapa.');
      return;
    }

    const { Arena, Limo, Arcilla } = selected;

    if (Arena === null || Limo === null || Arcilla === null) {
      setTexture('No se encontraron valores (Arena/Limo/Arcilla) en el feature seleccionado.');
      return;
    }

    const t = classifyUSDA(Arena, Limo, Arcilla);
    setTexture(t);

    const info = getTextureInterpretation(t);
    setTextureInfo(info);

    if (onSaveGranulometry) {
      onSaveGranulometry({
        arena: Arena,
        limo: Limo,
        arcilla: Arcilla,
        textura: t,
        interpretacion: info,
        layerName: selected.layerName,
        lonLat: lastClickLonLat ?? granulometria?.lonLat ?? null,
        featureId: selected.featureId ?? null,
        props: selected.props ?? {},
      });
    }
  };

  const onSaveCarta = () => {
    if (!selected || !onSaveCartaSuelo) return;

    const capabilityInterpretation = getCapabilityInterpretation(selected.props ?? {});

    onSaveCartaSuelo({
      layerName: selected.layerName,
      featureId: selected.featureId ?? null,
      props: selected.props ?? {},
      lonLat: lastClickLonLat ?? cartaSuelo?.lonLat ?? null,
      interpretation: capabilityInterpretation,
    });
  };

  const hasSelection = !!selected;
  const textureIsError =
    texture === 'Valores Inválidos' || texture === 'Clase No Determinada (Revisar Límites)';

  const visibleLayerNames = layerConfigs
    .filter((cfg) =>
      isGranulometriaMode ? cfg.key === 'granulometria' : cfg.key === 'cartas_suelo'
    )
    .map((cfg) => cfg.name);

  return (
    <section className='da-map-card mb-5'>
      <header className='da-map-card__header'>
        <div className='da-map-card__controls'>
          {Object.entries(layerStates)
            .filter(([name]) => visibleLayerNames.includes(name))
            .map(([name, visible]) => (
              <label key={name} className='da-toggle'>
                <input type='checkbox' checked={visible} onChange={() => toggleLayer(name)} />
                <span className='da-toggle__ui' />

                <span className='da-toggle__text d-inline-flex align-items-center gap-2'>
                  {name}

                  {layerLoadingState[name] === 'loading' && (
                    <span className='d-inline-flex align-items-center gap-1 text-muted'>
                      <Spinner
                        animation='border'
                        size='sm'
                        role='status'
                        aria-label='Cargando capa'
                      />
                      <span className='small'>Cargando capa...</span>
                    </span>
                  )}

                  {layerLoadingState[name] === 'error' && (
                    <span className='small text-danger'>Error al cargar</span>
                  )}
                </span>
              </label>
            ))}
        </div>
      </header>

      <div className='da-map-card__body'>
        <div className='da-ol-map' ref={mapRef} />
        <div id='popup' ref={popupRef} className='da-ol-popup' />
      </div>

      <footer className='da-map-card__footer'>
        <div className='da-panel'>
          <div className='da-panel__left'>
            {isGranulometriaMode ? (
              <>
                <button
                  className='da-btn da-btn--primary'
                  onClick={onCalculate}
                  disabled={!hasSelection}
                  type='button'
                  title={
                    !hasSelection
                      ? 'Seleccioná un punto/polígono en el mapa'
                      : 'Calcular textura USDA'
                  }
                >
                  Calcular Textura
                </button>

                <div className='da-hint'>
                  {hasSelection
                    ? 'Listo para calcular con los valores seleccionados.'
                    : 'Hacé click en el mapa para seleccionar valores.'}
                </div>
              </>
            ) : (
              <>
                <button
                  className='da-btn da-btn--primary'
                  onClick={onSaveCarta}
                  disabled={!hasSelection}
                  type='button'
                  title={
                    !hasSelection
                      ? 'Seleccioná un polígono en el mapa'
                      : 'Guardar carta de suelo seleccionada'
                  }
                >
                  Guardar Carta de Suelo
                </button>

                <div className='da-hint'>
                  {hasSelection
                    ? 'Carta de suelo seleccionada lista para guardar.'
                    : 'Hacé click en el mapa para seleccionar una carta de suelo.'}
                </div>
              </>
            )}
          </div>

          <div className='da-panel__right'>
            {isGranulometriaMode ? (
              <div className='da-kpis'>
                <div className='da-kpi'>
                  <div className='da-kpi__label'>Arena</div>
                  <div className='da-kpi__value'>{hasSelection ? selected.Arena ?? '—' : '—'}</div>
                </div>
                <div className='da-kpi'>
                  <div className='da-kpi__label'>Limo</div>
                  <div className='da-kpi__value'>{hasSelection ? selected.Limo ?? '—' : '—'}</div>
                </div>
                <div className='da-kpi'>
                  <div className='da-kpi__label'>Arcilla</div>
                  <div className='da-kpi__value'>
                    {hasSelection ? selected.Arcilla ?? '—' : '—'}
                  </div>
                </div>
              </div>
            ) : (
              <div className='da-kpis'>
                <div className='da-kpi'>
                  <div className='da-kpi__label'>Capa</div>
                  <div className='da-kpi__value'>
                    {hasSelection ? selected.layerName ?? '—' : '—'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {isGranulometriaMode && (
          <>
            <div
              className={`da-result ${texture ? 'da-result--ready' : ''} ${
                textureIsError ? 'da-result--warn' : ''
              }`}
            >
              <div className='da-result__label'>Textura</div>
              <div className='da-result__value'>{texture ?? '—'}</div>
            </div>

            {textureInfo && texture && (
              <div className='da-texture-card'>
                <div className='da-texture-card__header'>
                  <div className='da-texture-card__badge'>Interpretación - {textureInfo.title}</div>
                </div>

                {Array.isArray(textureInfo.bullets) && textureInfo.bullets.length > 0 && (
                  <ul className='da-texture-card__list ms-3'>
                    {textureInfo.bullets.map((b, i) => (
                      <li key={`b_${i}`}>{b}</li>
                    ))}
                  </ul>
                )}

                {Array.isArray(textureInfo.management) && textureInfo.management.length > 0 && (
                  <>
                    <div className='da-texture-card__badge'>Recomendaciones</div>
                    <ul className='da-texture-card__list da-texture-card__list--compact ms-3'>
                      {textureInfo.management.map((m, i) => (
                        <li key={`m_${i}`}>{m}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {!isGranulometriaMode && hasSelection && selected?.props && (
          <>
            <div className='da-texture-card'>
              <div className='da-texture-card__header'>
                <div className='da-texture-card__badge'>Datos de la Carta de Suelo</div>
              </div>

              <ul className='da-texture-card__list ms-3'>
                {sortCartaSueloEntries(
                  Object.entries(selected.props).filter(([key]) => key !== 'cap_uso')
                ).map(([key, value]) => (
                  <li key={key}>
                    <strong>{getAlias(selected.layerName, key)}:</strong> {String(value)}
                  </li>
                ))}
              </ul>
            </div>

            {(() => {
              const interpretation =
                selected?.interpretation ?? getCapabilityInterpretation(selected.props ?? {});

              const topBullets = Array.isArray(interpretation.bullets)
                ? interpretation.bullets.slice(0, 4)
                : [];

              const ipBullets = Array.isArray(interpretation.ipInfo?.bullets)
                ? interpretation.ipInfo.bullets.slice(0, 2)
                : [];

              return (
                <div className='da-texture-card'>
                  <div className='da-texture-card__header'>
                    <div className='da-texture-card__badge'>
                      Interpretación de Capacidad de Uso
                    </div>
                  </div>

                  <div className='mb-2'>
                    <ul className='da-texture-card__list ms-3'>
                      <li>
                        <strong>Capacidad de Uso:</strong> {interpretation.capUso || '—'}
                      </li>
                      <li>
                        <strong>Clase:</strong>{' '}
                        {interpretation.clase
                          ? `${interpretation.clase}`.trim()
                          : '—'}
                      </li>
                      <li>
                        <strong>Subclases:</strong>{' '}
                        {Array.isArray(interpretation.subclassInfoList) &&
                        interpretation.subclassInfoList.length > 0
                          ? interpretation.subclassInfoList.map((item) => item.code).join(', ')
                          : '—'}
                      </li>
                    </ul>
                  </div>

                  {interpretation.summary && (
                    <>
                      <div className='da-texture-card__badge'>Interpretación General</div>
                      <ul className='da-texture-card__list ms-3'>
                        <li>{interpretation.summary}</li>
                      </ul>
                    </>
                  )}

                  {topBullets.length > 0 && (
                    <>
                      <div className='da-texture-card__badge'>Aspectos Principales</div>
                      <ul className='da-texture-card__list ms-3'>
                        {topBullets.map((item, i) => (
                          <li key={`cap_b_${i}`}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {Array.isArray(interpretation.subclassInfoList) &&
                    interpretation.subclassInfoList.length > 0 && (
                    <>
                      <div className='da-texture-card__badge'>Limitaciones Dominantes</div>
                      <ul className='da-texture-card__list ms-3'>
                        {interpretation.subclassInfoList.map((item) => (
                          <li key={item.code}>
                            <strong>{item.code}</strong>: {item.title}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {interpretation.ipInfo && (
                    <>
                      <div className='da-texture-card__badge'>Índice de Productividad</div>
                      <ul className='da-texture-card__list ms-3'>
                        <li>
                          <strong>IP {interpretation.ipInfo.value ?? '—'}</strong>:{' '}
                          {interpretation.ipInfo.level}
                        </li>
                        {interpretation.ipInfo.summary && <li>{interpretation.ipInfo.summary}</li>}
                        {ipBullets.map((b, i) => (
                          <li key={`ip_b_${i}`}>{b}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {Array.isArray(interpretation.management) &&
                    interpretation.management.length > 0 && (
                    <>
                      <div className='da-texture-card__badge'>Recomendaciones de Manejo</div>
                      <ul className='da-texture-card__list da-texture-card__list--compact ms-3'>
                        {interpretation.management.map((item, i) => (
                          <li key={`cap_m_${i}`}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </footer>
    </section>
  );
}